import { buildStableHash } from './shared/stableHash';
import type { ChecklistCategoryId, WithChecklistItemMetadata } from './types';

const EVIDENCE_HASH_VERSION = 'ev2';

const NON_EVIDENCE_FIELDS = new Set([
  'canOverride',
  'description',
  'evidenceHash',
  'field',
  'itemKey',
  'label',
  'linkedDealOptions',
  'name',
  'rule',
  'source',
  'sourceLinks',
  'status',
  'statusLabel',
  'summary',
  'title',
]);

const CHECKLIST_ITEM_KEY_AREAS: Record<ChecklistCategoryId, string> = {
  blackoutDates: 'blackout',
  bundledExperiences: 'bundled_experiences',
  campaign: 'campaign',
  discountsMargin: 'discounts_margin',
  dealOptionsInclusions: 'deal_options',
  egs: 'egs',
  setup: 'setup',
  surcharges: 'surcharge',
  taxesPayments: 'taxes_payments',
};

export function withChecklistItemMetadata<Row extends { id: string }>(
  categoryId: ChecklistCategoryId,
  rows: Array<Row>,
): Array<WithChecklistItemMetadata<Row>> {
  return rows.map((row) => ({
    ...row,
    evidenceHash: buildEvidenceHash(row),
    itemKey: buildItemKey(categoryId, row.id),
  }));
}

function buildItemKey(categoryId: ChecklistCategoryId, rowId: string) {
  return `${CHECKLIST_ITEM_KEY_AREAS[categoryId]}.${normalizeKeyPart(rowId)}`;
}

function buildEvidenceHash(row: Record<string, unknown>) {
  return `${EVIDENCE_HASH_VERSION}_${buildStableHash(normalizeEvidence(row))}`;
}

function normalizeEvidence(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(normalizeEvidence);
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([key]) => !NON_EVIDENCE_FIELDS.has(key))
        .map(([key, entryValue]) => [key, normalizeEvidence(entryValue)]),
    );
  }

  return value;
}

function normalizeKeyPart(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9:_/+.]+/g, '_')
    .replace(/^_+|_+$/g, '');
}
